import { useEffect, useRef, useState } from "react"
import { Volume2, VolumeX, Play, ChevronDown, ChevronUp } from "lucide-react"

const WELCOME_SCRIPT =
  "Welcome to EarlyScape! I'm here to help you discover your path. Let's take a few minutes to learn about you and set you up for success. Your future starts right here."

const SAMPLE_TEXT = "Hi! I'm here to guide you on your journey to greatness."

const STORAGE_KEY = "earlyscape_voice_prefs"

// ─── Known human-quality voice definitions ────────────────────────────────────
// patterns[] is matched against the system voice name (case-insensitive)
const FEATURED_VOICES = [
  // Girls
  { key: "nicky",    name: "Nicky",    gender: "f", accent: "en-US", color: "#8B5CF6", patterns: [/nicky/i] },
  { key: "samantha", name: "Samantha", gender: "f", accent: "en-US", color: "#EC4899", patterns: [/samantha/i] },
  { key: "karen",    name: "Karen",    gender: "f", accent: "en-AU", color: "#06B6D4", patterns: [/karen/i] },
  { key: "zira",     name: "Zira",     gender: "f", accent: "en-US", color: "#F43F5E", patterns: [/zira/i] },
  { key: "hazel",    name: "Hazel",    gender: "f", accent: "en-GB", color: "#A855F7", patterns: [/hazel/i] },
  { key: "victoria", name: "Victoria", gender: "f", accent: "en-US", color: "#0EA5E9", patterns: [/victoria/i] },
  // Boys
  { key: "alex",    name: "Alex",    gender: "m", accent: "en-US", color: "#3B82F6", patterns: [/\balex\b/i] },
  { key: "daniel",  name: "Daniel",  gender: "m", accent: "en-GB", color: "#10B981", patterns: [/daniel/i] },
  { key: "david",   name: "David",   gender: "m", accent: "en-US", color: "#F59E0B", patterns: [/\bdavid\b/i, /microsoft david/i] },
  { key: "tom",     name: "Tom",     gender: "m", accent: "en-GB", color: "#EF4444", patterns: [/\btom\b/i] },
  { key: "mark",    name: "Mark",    gender: "m", accent: "en-US", color: "#6366F1", patterns: [/\bmark\b/i, /microsoft mark/i] },
  { key: "james",   name: "James",   gender: "m", accent: "en-AU", color: "#14B8A6", patterns: [/james/i] },
]

// Filter robotic/non-human voices by exclusion patterns
const ROBOTIC_PATTERNS = [/espeak/i, /mbrola/i, /festival/i, /flite/i, /pico/i]
const isHumanVoice = (v) =>
  v.lang.startsWith("en") &&
  !ROBOTIC_PATTERNS.some((p) => p.test(v.name))

// Map a featured definition to an available system voice
const matchFeatured = (def, available) =>
  available.find((v) => def.patterns.some((p) => p.test(v.name))) ?? null

// Voices not matched by any featured definition
const getExtraVoices = (available) =>
  available.filter(
    (v) => !FEATURED_VOICES.some((def) => def.patterns.some((p) => p.test(v.name)))
  )

const SPEED_OPTIONS = [
  { label: "Slow",   value: 0.78 },
  { label: "Normal", value: 1.0  },
  { label: "Fast",   value: 1.22 },
]

function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") } catch { return null }
}
function savePrefs(p) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)) } catch {}
}

// ─── Voice card ────────────────────────────────────────────────────────────────
function VoiceCard({ def, systemVoice, selected, onSelect, onPreview }) {
  const available = !!systemVoice
  const accentLabel = (def?.accent ?? systemVoice?.lang ?? "").replace("en-", "").toUpperCase()
  const displayName = def?.name ?? systemVoice?.name.split(" ").slice(-1)[0] ?? "Voice"
  const color = def?.color ?? "#6366F1"
  const initial = displayName.charAt(0).toUpperCase()
  const isFemale = def?.gender === "f"

  return (
    <button
      disabled={!available}
      onClick={() => available && onSelect()}
      className={`relative flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all text-center
        ${selected
          ? "border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-100"
          : available
            ? "border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm"
            : "border-slate-100 bg-slate-50 opacity-40 cursor-not-allowed"}`}
    >
      {/* Avatar circle */}
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center text-white font-black text-base shrink-0 shadow-sm"
        style={{ backgroundColor: available ? color : "#94A3B8" }}
      >
        {initial}
      </div>

      <div className="space-y-0.5">
        <p className="text-xs font-bold text-slate-800 leading-tight">{displayName}</p>
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{accentLabel}</span>
      </div>

      {/* Gender tag */}
      <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full
        ${isFemale ? "bg-pink-100 text-pink-600" : "bg-blue-100 text-blue-600"}`}>
        {isFemale ? "Girl" : "Boy"}
      </span>

      {/* Preview button */}
      {available && (
        <button
          onClick={(e) => { e.stopPropagation(); onPreview() }}
          title="Preview"
          className="absolute top-2 right-2 w-5 h-5 rounded-full bg-slate-100 hover:bg-indigo-100 flex items-center justify-center transition-all"
        >
          <Play className="w-2.5 h-2.5 text-slate-500 hover:text-indigo-600" />
        </button>
      )}

      {selected && (
        <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center">
          <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="none">
            <path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </button>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────
export function WelcomeStep({ onNext }) {
  const [speaking, setSpeaking] = useState(false)
  const [voices, setVoices] = useState([])                 // human-filtered system voices
  const [selectedVoiceName, setSelectedVoiceName] = useState("")
  const [speed, setSpeed] = useState(1.0)
  const [showMore, setShowMore] = useState(false)

  const synthRef = useRef(null)

  // ── Load saved prefs ──────────────────────────────────────────────────────
  useEffect(() => {
    const saved = loadPrefs()
    if (saved?.voiceName)  setSelectedVoiceName(saved.voiceName)
    if (saved?.speed)      setSpeed(saved.speed)
  }, [])

  // ── Init synth + load voices ──────────────────────────────────────────────
  useEffect(() => {
    const synth = window.speechSynthesis
    if (!synth) return
    synthRef.current = synth

    const load = () => {
      const humanVoices = synth.getVoices().filter(isHumanVoice)
      setVoices(humanVoices)
      setSelectedVoiceName((prev) => {
        if (prev && humanVoices.some((v) => v.name === prev)) return prev
        // Prefer Nicky if available, else first available
        const nicky = humanVoices.find((v) => /nicky/i.test(v.name))
        return (nicky ?? humanVoices[0])?.name ?? ""
      })
    }

    load()
    synth.addEventListener("voiceschanged", load)
    setTimeout(() => speak(WELCOME_SCRIPT), 500)

    return () => {
      synth.removeEventListener("voiceschanged", load)
      synth.cancel()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getVoiceObj = (name) => voices.find((v) => v.name === (name ?? selectedVoiceName)) ?? null

  const speak = (text, voiceName, overrideSpeed) => {
    const synth = synthRef.current
    if (!synth) return
    synth.cancel()
    const u = new SpeechSynthesisUtterance(text)
    const voice = getVoiceObj(voiceName)
    if (voice) u.voice = voice
    u.rate  = overrideSpeed ?? speed
    u.pitch = 1.0
    u.onstart = () => setSpeaking(true)
    u.onend   = () => setSpeaking(false)
    u.onerror = () => setSpeaking(false)
    synth.speak(u)
  }

  const stop = () => { synthRef.current?.cancel(); setSpeaking(false) }

  const selectVoice = (voiceName) => {
    setSelectedVoiceName(voiceName)
    savePrefs({ voiceName, speed })
    speak(SAMPLE_TEXT, voiceName)
  }

  const selectSpeed = (val) => {
    setSpeed(val)
    savePrefs({ voiceName: selectedVoiceName, speed: val })
  }

  // Build featured card list — 3 girls then 3 boys (only available ones)
  const featuredCards = FEATURED_VOICES
    .map((def) => ({ def, systemVoice: matchFeatured(def, voices) }))
    .filter(({ def, systemVoice }) => systemVoice !== null)  // only show available
    .slice(0, 6)

  const extraVoices = getExtraVoices(voices)

  return (
    <div className="flex flex-col items-center text-center gap-6 py-4">
      {/* Logo */}
      <div className="w-20 h-20 rounded-3xl bg-linear-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-xl shadow-indigo-200">
        <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none">
          <circle cx="24" cy="24" r="20" fill="white" fillOpacity={0.15} />
          <path d="M14 32 C14 22 24 14 34 17" stroke="white" strokeWidth="3" strokeLinecap="round" />
          <path d="M34 17 C37 27 29 37 19 37" stroke="white" strokeWidth="3" strokeLinecap="round" />
          <circle cx="24" cy="24" r="4" fill="white" />
        </svg>
      </div>

      {/* Heading */}
      <div className="space-y-2">
        <h1 className="text-3xl font-black text-slate-900 leading-tight">Welcome to EarlyScape</h1>
        <p className="text-slate-500 text-sm max-w-xs leading-relaxed">
          Your personalised career discovery platform. Let&apos;s set you up for success.
        </p>
      </div>

      {/* Script card */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 max-w-sm w-full">
        <p className="text-indigo-800 text-sm font-medium leading-relaxed italic">
          &ldquo;{WELCOME_SCRIPT}&rdquo;
        </p>
      </div>

      {/* Playback controls */}
      <div className="flex items-center gap-2">
        {speaking ? (
          <button
            onClick={stop}
            className="flex items-center gap-2 text-sm font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 px-4 py-2 rounded-full transition-all"
          >
            <VolumeX className="w-4 h-4" />
            Stop
          </button>
        ) : (
          <button
            onClick={() => speak(WELCOME_SCRIPT)}
            className="flex items-center gap-2 text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-full transition-all"
          >
            <Volume2 className="w-4 h-4" />
            {synthRef.current ? "Replay" : "Voice unavailable"}
          </button>
        )}

        {/* Speed pills */}
        {synthRef.current && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
            {SPEED_OPTIONS.map((opt) => (
              <button
                key={opt.label}
                onClick={() => selectSpeed(opt.value)}
                className={`text-xs font-semibold px-3 py-1 rounded-full transition-all ${
                  speed === opt.value
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Voice selector ─────────────────────────────────────────────────── */}
      {featuredCards.length > 0 && (
        <div className="w-full max-w-sm space-y-3">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Choose a voice</p>

          {/* Featured grid */}
          <div className="grid grid-cols-3 gap-2">
            {featuredCards.map(({ def, systemVoice }) => (
              <VoiceCard
                key={def.key}
                def={def}
                systemVoice={systemVoice}
                selected={selectedVoiceName === systemVoice?.name}
                onSelect={() => selectVoice(systemVoice.name)}
                onPreview={() => speak(SAMPLE_TEXT, systemVoice.name)}
              />
            ))}
          </div>

          {/* More voices */}
          {extraVoices.length > 0 && (
            <>
              <button
                onClick={() => setShowMore((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 mx-auto transition-all"
              >
                {showMore ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {showMore ? "Show less" : `${extraVoices.length} more voice${extraVoices.length > 1 ? "s" : ""}`}
              </button>

              {showMore && (
                <div className="grid grid-cols-3 gap-2">
                  {extraVoices.map((v) => (
                    <VoiceCard
                      key={v.name}
                      def={null}
                      systemVoice={v}
                      selected={selectedVoiceName === v.name}
                      onSelect={() => selectVoice(v.name)}
                      onPreview={() => speak(SAMPLE_TEXT, v.name)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-col items-center gap-2 w-full max-w-sm">
        <button
          onClick={onNext}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-8 py-3 rounded-xl text-sm transition-all active:scale-95 shadow-lg shadow-indigo-100"
        >
          Let&apos;s Get Started
        </button>
        <button
          onClick={onNext}
          className="text-xs text-slate-400 hover:text-slate-600 font-medium transition-colors py-1"
        >
          Skip intro
        </button>
      </div>
    </div>
  )
}
